import {
  Button,
  Card,
  Col,
  Drawer,
  Flex,
  Row,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CreditCard } from 'lucide-react';
import { useState } from 'react';

import {
  useChangeBillingPlan,
  useCreateBillingTopUpCheckoutSession,
  useGetBillingPlanTiers,
  useGetBillingUpgradePreview,
  useUpgradeBillingSubscription,
} from '../../../api/generated/api';
import { TextTertiary } from '../../../ui/components/text';
import { formatDate } from '../../../ui/utils/format-date';
import { useRefreshBillingContext } from '../hooks/useRefreshBillingContext';
import { TOP_UP_BLOCKED_COPY, formatCredits } from '../shared/billing-format';
import { TRIAL_DAYS } from '../shared/trial';

import { DiscountTags, DiscountedPrice } from './DiscountedPrice';
import { PaymentMethodText } from './PaymentMethodText';
import { PlanSelect } from './PlanSelect';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';

export type ManagePlanDrawerProps = {
  open: boolean;
  organizationId: string;
  overview: GetBillingOverviewResponseDto;
  portalLoading: boolean;
  resubscribeLoading: boolean;
  onOpenPortal: () => void;
  /** Starts a Checkout for an ended plan; `priceId` only when the customer picked another plan. */
  onResubscribe: (priceId?: string) => void;
  onClose: () => void;
};

export const ManagePlanDrawer = ({
  open,
  organizationId,
  overview,
  portalLoading,
  resubscribeLoading,
  onOpenPortal,
  onResubscribe,
  onClose,
}: ManagePlanDrawerProps) => {
  const refreshBillingContext = useRefreshBillingContext();
  const checkoutMutation = useCreateBillingTopUpCheckoutSession({
    mutation: { onSuccess: (result) => window.location.assign(result.data.url) },
  });
  const [stayOnTrial, setStayOnTrial] = useState(false);
  // The plan the customer picked in this drawer; undefined until they touch the select.
  const [pickedPriceId, setPickedPriceId] = useState<string>();

  const { subscription, plan, credits, topUp, paymentMethod, discounts } = overview;
  const trialing = subscription?.status === 'trialing';
  const ended = subscription?.status === 'cancelled' || subscription?.status === 'suspended';
  // A paid plan moves to another tier from its next period; not while past due or about to end.
  const changeable = subscription?.status === 'active' && !subscription.cancelledAt;
  const upgrading = trialing && !stayOnTrial;
  const blocked = topUp?.blockedReason ?? null;

  const tiersQuery = useGetBillingPlanTiers(organizationId, {
    query: { enabled: open && (trialing || ended || changeable), staleTime: 5 * 60_000 },
  });
  const sellableTiers = tiersQuery.data?.data.tiers ?? [];
  // An organization can sit on a price that is no longer for sale: it stays in the list as its own.
  const tiers =
    sellableTiers.some((tier) => tier.priceId === plan.priceId) ||
    plan.priceCents === null ||
    plan.credits === null
      ? sellableTiers
      : [
          {
            priceId: plan.priceId,
            amountCents: plan.priceCents,
            discountedAmountCents: plan.discountedPriceCents ?? plan.priceCents,
            currency: plan.currency,
            interval: 'month',
            credits: plan.credits,
          },
          ...sellableTiers,
        ];
  const selectedPriceId = pickedPriceId ?? plan.priceId;
  const selectedTier = tiers.find((tier) => tier.priceId === selectedPriceId);
  const selectedPriceCents = selectedTier?.amountCents ?? plan.priceCents;
  const selectedDiscountedCents = selectedTier?.discountedAmountCents ?? plan.discountedPriceCents;
  const selectedCredits = selectedTier?.credits ?? plan.credits ?? 0;
  const changing = changeable && pickedPriceId !== undefined && pickedPriceId !== plan.priceId;
  // The price was already moved (here or by K3X support): this month's credits are the old plan's.
  const pendingChange =
    changeable &&
    !changing &&
    credits.plan?.kind === 'plan' &&
    plan.credits !== null &&
    credits.plan.included !== plan.credits;

  const previewQuery = useGetBillingUpgradePreview(
    organizationId,
    pickedPriceId ? { priceId: pickedPriceId } : undefined,
    { query: { enabled: open && trialing, staleTime: 0 } },
  );
  const preview = previewQuery.data?.data;

  const changeMutation = useChangeBillingPlan({
    mutation: {
      onSuccess: async () => {
        message.success(
          `Your plan changes on ${formatDate(subscription?.currentPeriodEnd)}. Nothing is charged today.`,
        );
        await refreshBillingContext(organizationId);
        setPickedPriceId(undefined);
      },
      onError: () => message.error('Could not change the plan'),
    },
  });

  const upgradeMutation = useUpgradeBillingSubscription({
    mutation: {
      onSuccess: async (res) => {
        if (!res.data.paid) {
          if (res.data.invoiceUrl) {
            message.warning('Confirm the payment on Stripe');
            window.open(res.data.invoiceUrl, '_blank', 'noopener');
          } else {
            message.error('Card declined');
          }
          return;
        }

        message.success("You're on the paid plan");
        await refreshBillingContext(organizationId);
        window.dispatchEvent(new CustomEvent('billing:upgradeResolved'));
        onClose();
      },
      onError: () => message.error('Upgrade failed'),
    },
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      afterOpenChange={(next) => {
        if (!next) {
          setStayOnTrial(false);
          setPickedPriceId(undefined);
        }
      }}
      title="Manage plan"
      size="min(480px, calc(100vw - 40px))"
      footer={
        <Flex align="center" justify="space-between" gap={8}>
          {upgrading && (previewQuery.isPending || preview) ? (
            <Flex align="center" gap={6}>
              <TextTertiary style={{ fontSize: 13 }}>Due today</TextTertiary>
              {preview ? (
                <DiscountedPrice
                  cents={discounts.length > 0 ? selectedPriceCents : preview.amountDueCents}
                  discountedCents={preview.amountDueCents}
                  className="font-mono"
                  style={{ fontSize: 13, fontWeight: 600 }}
                  testId="upgrade-due"
                />
              ) : (
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 64, minWidth: 64, height: 16 }}
                />
              )}
            </Flex>
          ) : !trialing && !ended ? (
            <Button type="text" loading={portalLoading} onClick={onOpenPortal}>
              {subscription?.cancelledAt ? 'Reactivate subscription' : 'Cancel subscription'}
            </Button>
          ) : (
            <span />
          )}
          <Flex gap={8}>
            <Button onClick={onClose} style={{ fontSize: 13 }}>
              Close
            </Button>
            {upgrading && (
              <Button
                type="primary"
                loading={upgradeMutation.isPending}
                onClick={() =>
                  upgradeMutation.mutate({ organizationId, data: { priceId: pickedPriceId } })
                }
                data-testid="upgrade-confirm"
                style={{ fontSize: 13 }}
              >
                Upgrade
              </Button>
            )}
            {changing && (
              <Button
                type="primary"
                loading={changeMutation.isPending}
                onClick={() =>
                  changeMutation.mutate({ organizationId, data: { priceId: pickedPriceId } })
                }
                data-testid="plan-change-confirm"
                style={{ fontSize: 13 }}
              >
                Change plan
              </Button>
            )}
            {ended && (
              <Button
                type="primary"
                loading={resubscribeLoading}
                onClick={() => onResubscribe(pickedPriceId)}
                data-testid="plan-resubscribe"
                style={{ fontSize: 13 }}
              >
                Re-subscribe
              </Button>
            )}
          </Flex>
        </Flex>
      }
    >
      <Flex vertical gap={18}>
        <Flex vertical gap={10}>
          {[
            ...(trialing
              ? [
                  {
                    title: 'Trial',
                    pickable: false,
                    price: '$0',
                    description: `${formatCredits(credits.plan?.included ?? 0)} credits for ${TRIAL_DAYS} days. No top-ups, no auto-recharge.`,
                    current: true,
                    selected: !upgrading,
                    onClick: () => setStayOnTrial(true),
                  },
                ]
              : []),
            {
              title: 'K3X',
              price: <DiscountedPrice cents={selectedPriceCents} discountedCents={selectedDiscountedCents} suffix=" / mo" />,
              description: `${formatCredits(selectedCredits)} credits a month, unlimited members, top-ups and auto-recharge. Cancel anytime.`,
              current: !trialing && !ended && !changing,
              selected: !trialing || upgrading,
              onClick: trialing ? () => setStayOnTrial(false) : undefined,
              pickable: trialing || ended || changeable,
            },
          ].map((option) => (
            <Card
              key={option.title}
              hoverable={Boolean(option.onClick)}
              onClick={option.onClick}
              style={{
                borderRadius: 20,
                borderColor: option.selected ? 'var(--ant-color-primary)' : undefined,
              }}
              styles={{ body: { padding: '16px 18px' } }}
            >
              <Flex vertical gap={10}>
                <Flex align="center" justify="space-between" gap={8}>
                  <Typography.Text strong style={{ fontSize: 15 }}>
                    {option.title}
                  </Typography.Text>
                  <Flex align="center" gap={8}>
                    {option.current && (
                      <Tag
                        variant="filled"
                        color="cyan"
                        style={{
                          padding: '4px 9px',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        Current
                      </Tag>
                    )}
                    <Typography.Text strong className="font-mono" style={{ fontSize: 15 }} data-testid="plan-option-price">
                      {option.price}
                    </Typography.Text>
                  </Flex>
                </Flex>
                <TextTertiary style={{ fontSize: 13 }}>{option.description}</TextTertiary>
                {option.title === 'K3X' && discounts.length > 0 && (
                  <Flex gap={6} wrap>
                    <DiscountTags discounts={discounts} detailed />
                  </Flex>
                )}
                {option.pickable && tiers.length > 1 && (
                  <PlanSelect
                    tiers={tiers}
                    value={selectedPriceId}
                    onChange={(priceId) => {
                      setPickedPriceId(priceId);
                      setStayOnTrial(false);
                    }}
                    disabled={upgradeMutation.isPending || changeMutation.isPending}
                    testId="plan-select"
                  />
                )}
                {option.pickable && changing && (
                  <TextTertiary style={{ fontSize: 12 }} data-testid="plan-change-note">
                    Starts {formatDate(subscription?.currentPeriodEnd)}, with your next invoice. Nothing
                    is charged today. Need credits sooner? Buy a top-up.
                  </TextTertiary>
                )}
                {option.pickable && pendingChange && (
                  <TextTertiary style={{ fontSize: 12 }} data-testid="plan-pending-note">
                    Starts {formatDate(subscription?.currentPeriodEnd)}. Until then you keep this
                    month's {formatCredits(credits.plan?.included ?? 0)} credits.
                  </TextTertiary>
                )}
              </Flex>
            </Card>
          ))}
        </Flex>

        {topUp && topUp.packs.length > 0 && (
          <Flex vertical gap={8}>
            <Typography.Text strong style={{ fontSize: 13 }}>
              One-time top-up
            </Typography.Text>
            <Tooltip title={blocked && TOP_UP_BLOCKED_COPY[blocked]}>
              <Row gutter={[8, 8]}>
                {topUp.packs.map((pack) => (
                  <Col key={pack.priceId} span={8}>
                    <Button
                      block
                      disabled={blocked !== null || checkoutMutation.isPending}
                      loading={
                        checkoutMutation.isPending &&
                        checkoutMutation.variables.data.priceId === pack.priceId
                      }
                      onClick={() =>
                        checkoutMutation.mutate({ organizationId, data: { priceId: pack.priceId } })
                      }
                      style={{
                        height: 'auto',
                        padding: '11px 12px',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Flex vertical align="flex-start" gap={3}>
                        <Typography.Text strong className="font-mono">
                          {formatCredits(pack.credits)}
                        </Typography.Text>
                        <DiscountedPrice
                          cents={pack.priceCents}
                          discountedCents={pack.discountedPriceCents}
                          style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}
                        />
                      </Flex>
                    </Button>
                  </Col>
                ))}
              </Row>
            </Tooltip>
          </Flex>
        )}

        <Flex vertical gap={7}>
          <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>
            Payment method
          </Typography.Text>
          <Card style={{ borderRadius: 14 }} styles={{ body: { padding: '11px 14px' } }}>
            <Flex align="center" gap={12}>
              <CreditCard size={15} color="var(--ant-color-text-tertiary)" />
              <Flex flex={1}>
                <Typography.Text style={{ fontSize: 13 }}>
                  {paymentMethod ? <PaymentMethodText method={paymentMethod} /> : 'No card on file'}
                </Typography.Text>
              </Flex>
              <Button
                type="text"
                loading={portalLoading}
                onClick={onOpenPortal}
                style={{
                  height: 'auto',
                  padding: '4px 9px',
                  fontSize: 12,
                  color: 'var(--ant-color-text-secondary)',
                }}
              >
                {paymentMethod ? 'Change' : 'Add card'}
              </Button>
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </Drawer>
  );
};
